import React from 'react';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTextSizeClass, getHeaderTextSizeClass } from '../../utils/fontSizeUtils';
import { TABLE_STYLES } from '../../styles/tableStyles';
import { HISTORICAL_PRICE_STATISTICS } from '../../utils/dailyPriceUtils';
import { formatDailyPriceStatisticValue } from '../../utils/dailyPriceUtils';

export interface TickerDailyData {
  date: string;
  close_price_adjusted: number;
  num_transactions: number;
  volume_adjusted: number;
}

interface DailyPriceTableProps {
  data: TickerDailyData[];
  loading: boolean;
  loadingMore: boolean;
  sortColumn: 'date' | 'close_price_adjusted' | 'num_transactions' | 'volume_adjusted';
  sortDirection: 'asc' | 'desc';
  onSort: (column: 'date' | 'close_price_adjusted' | 'num_transactions' | 'volume_adjusted') => void;
  onFullScreenClick: () => void;
  tableRef?: React.RefObject<HTMLDivElement>;
  isSyncing?: boolean;
}

export const DailyPriceTable: React.FC<DailyPriceTableProps> = ({
  data,
  loading,
  loadingMore,
  sortColumn,
  sortDirection,
  onSort,
  onFullScreenClick,
  tableRef,
  isSyncing = false,
}) => {
  const { fontSize } = useFontSize();
  
  const renderSortIcon = (statisticKey: string) => {
    if (sortColumn === statisticKey) {
      return sortDirection === 'desc' ? (
        <svg
          className="w-4 h-4 text-highlight-gold"
          fill="currentColor"
          viewBox="0 0 20 20"
          style={{ minWidth: '16px' }}
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg
          className="w-4 h-4 text-highlight-gold"
          fill="currentColor"
          viewBox="0 0 20 20"
          style={{ minWidth: '16px' }}
        >
          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
        </svg>
      );
    } else {
      return (
        <svg
          className="w-4 h-4 text-gray-400"
          fill="currentColor"
          viewBox="0 0 20 20"
          style={{ minWidth: '16px' }}
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    }
  };

  const cellClassName = (statisticKey: string) => {
    return `${statisticKey === 'date' ? 'pl-4 pr-3 sm:pr-4 md:pr-3 lg:pl-5 lg:pr-4' : 'px-4 md:px-4 lg:px-6'} whitespace-nowrap font-semibold ${getTextSizeClass(fontSize)} h-12 sm:h-16 bg-surface-light dark:bg-background-card ${statisticKey === 'date' ? 'transition-none' : ''}`;
  };

  const headerCellClassName = (statisticKey: string) => {
    return `py-1 ${statisticKey === 'date' ? 'pl-4 pr-3 sm:pr-4 md:pr-3 lg:pl-5 lg:pr-4' : 'px-4 md:px-4 lg:px-6'} text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 bg-gray-50 dark:bg-background-card transition-none`;
  };

  return (
    <div ref={tableRef} className={TABLE_STYLES.tableWrapper}>
      <table className={TABLE_STYLES.table} style={{ tableLayout: 'auto', width: '100%' }}>
        <colgroup>
          <col />
          <col />
          <col />
          <col />
        </colgroup>
        <thead className="bg-gray-50 dark:bg-background-card sticky-header-border" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
          <tr>
            {HISTORICAL_PRICE_STATISTICS.map((statistic) => (
              <th
                key={statistic.key}
                className={headerCellClassName(statistic.key)}
                style={{
                  ...(statistic.key === 'date' ? { position: 'sticky', left: 0, zIndex: 40 } : {})
                }}
                onClick={() => onSort(statistic.key as typeof sortColumn)}
              >
                <div className="flex items-center">
                  <span>{statistic.label}</span>
                  <div className="ml-1 w-5 h-4 flex items-center justify-center">
                    {renderSortIcon(statistic.key)}
                  </div>
                </div>
              </th>
            ))}
            
            {/* Full screen button column */}
            <th
              className="py-1 px-4 text-right bg-gray-50 dark:bg-background-card"
              style={{ position: 'sticky', right: 0, zIndex: 40 }}
            >
              <button
                onClick={onFullScreenClick}
                className="p-1.5 text-gray-500 dark:text-white hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                title="Open in full screen"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              </button>
            </th>
          </tr>
        </thead>
        <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
          {(isSyncing || (loading && data.length === 0)) ? (
            // Show placeholder rows with loading animation
            Array.from({ length: 20 }).map((_, index) => (
              <tr key={`loading-${index}`} className="bg-surface-light dark:bg-background-card">
                {HISTORICAL_PRICE_STATISTICS.map((statistic) => (
                  <td
                    key={statistic.key}
                    className={cellClassName(statistic.key)}
                    style={{
                      ...(statistic.key === 'date' ? { position: 'sticky', left: 0, zIndex: 30 } : {})
                    }}
                  >
                    <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 rounded" style={{ width: statistic.key === 'date' ? '80px' : '60px' }}></div>
                  </td>
                ))}
                
                {/* Empty cell for full screen button column */}
                <td
                  className={`py-1 sm:py-2 px-4 text-gray-900 dark:text-text-primary font-semibold ${getTextSizeClass(fontSize)} bg-surface-light dark:bg-background-card`}
                  style={{ position: 'sticky', right: 0, zIndex: 30 }}
                >
                  {/* Empty cell for full screen button column */}
                </td>
              </tr>
            ))
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={HISTORICAL_PRICE_STATISTICS.length + 1} className="px-4 py-8 text-center text-gray-600 dark:text-text-secondary">
                No data available
              </td>
            </tr>
          ) : (
            data.map((rowData, index) => (
              <tr key={`${rowData.date}-${index}`} className="bg-surface-light dark:bg-background-card">
                {HISTORICAL_PRICE_STATISTICS.map((statistic) => (
                  <td
                    key={statistic.key}
                    className={`${cellClassName(statistic.key)} text-text-light-neutral dark:text-text-secondary`}
                    style={{
                      ...(statistic.key === 'date' ? { position: 'sticky', left: 0, zIndex: 30 } : {})
                    }}
                  >
                    {formatDailyPriceStatisticValue(statistic.key, rowData, false)}
                  </td>
                ))}
                
                {/* Empty cell for full screen button column */}
                <td
                  className={`py-1 sm:py-2 px-4 text-gray-900 dark:text-text-primary font-semibold ${getTextSizeClass(fontSize)} bg-surface-light dark:bg-background-card`}
                  style={{ position: 'sticky', right: 0, zIndex: 30 }}
                >
                  {/* Empty cell for full screen button column */}
                </td>
              </tr>
            ))
          )}
          {loadingMore && (
            <tr>
              <td colSpan={HISTORICAL_PRICE_STATISTICS.length + 1} className="px-4 py-4 text-center">
                <div className="animate-pulse text-gray-600 dark:text-text-secondary">Loading more...</div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

